// src/ContractPage.tsx
import { formatStateCode } from "./shared/format";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./ContractPage.css";
import { formatLongDate, formatShortDate, type YearGuess } from "./utils/contractDate";
import SignatureImage from "./components/Contract/SignatureImage";
import {
  contractLine,
  normalizeContractText,
  structureKeyFor,
  type ContractText,
  type ContractTextKey,
} from "./shared/contractText";
import StandardLineEditor from "./components/Contract/StandardLineEditor";
import {
  ST124Form,
  type ST124FormProps,
  wrapDescriptionLines,
  MAX_DESC_LINE,
  MAX_DESC_LINES,
} from "./components/Contract/ST124Form";

/** One row of the staged payment schedule. `pct` is the canonical value
 *  (percentage of the contract total); the dollar amount is always derived
 *  as round(total * pct / 100), so the two can never drift. The Balance row
 *  is NOT a stage — it is the implicit remainder (total − Σ stage amounts). */
export type PaymentStage = { id: string; label: string; pct: number };

export type ContractData = {
  // Client / header
  hdrClient: string;
  hdrAddress: string;
  hdrCity: string;
  hdrState: string;
  hdrZip: string;
  hdrPhone: string;
  hdrEmail: string;
  hdrDate: string;
  // Set once the user manually edits the Contract date, pinning it against the
  // save-time refresh-to-today (App.withRefreshedContractDate).
  hdrDateTouched: boolean;
  hdrPageNum: string;
  hdrPageOf: string;
  // Tracks which header fields the user manually edited
  // so estimate-driven auto-fill doesn't clobber them.
  hdrTouched: {
    client?: boolean;
    address?: boolean;
    city?: boolean;
    state?: boolean;
    zip?: boolean;
    phone?: boolean;
    email?: boolean;
  };
  // Schedule
  hdrApproxStart: string;
  hdrApproxEnd: string;
  hdrEssence: "yes" | "not" | "";
  // Scope
  specificationText: string;
  specificationTouched: boolean;
  // Contract sum
  contractSumWords: string;
  contractSumNumerals: string;
  // Payment
  paymentScheduleText: string;
  paymentMode: "basic" | "staged";
  // Dynamic staged schedule. Optional so pre-existing contracts (which only
  // have the legacy fixed paymentPercents/paymentLabels below) still load —
  // getStages() derives the array from the legacy fields when this is absent.
  paymentStages?: PaymentStage[];
  // Legacy fixed-stage schedule. Retained for backward-compat deserialization
  // and as the fallback source for getStages(). paymentLabels.balance is still
  // the active label for the Balance remainder row.
  paymentPercents: {
    deposit: number;
    dayOne: number;
    afterDecking: number;
    holdback: number;
  };
  paymentLabels: {
    deposit: string;
    dayOne: string;
    afterDecking: string;
    holdback: string;
    balance: string;
  };
  // Legal
  legalDisclaimerText: string;
  // Display
  forcePageBreak: boolean;
  // ST-124 (Capital Improvement)
  st124ProjectDescription: string;
  st124ProjectDescriptionTouched: boolean;
};

type Props = {
  estimateId: string;
  orgId: string | null;
  contract: ContractData | null;
  /** `meta.auto` marks the page's own auto-fill (client header, spec text,
   *  ST-124 description), rebuilt from the estimate every time the page
   *  opens. App stores it without flagging the estimate unsaved. */
  onContractChange: (next: ContractData, meta?: { auto?: boolean }) => void;
  finalEstimate: number;
  selectedDecking: any;
  selectedRailing: any;
  selectedStairOption: any;
  selectedFastener: any;
  selectedConstruction: any;
  selectedSkirting?: any;
  constructionKey?: string;
  constructionType?: string;
  clientFirstName?: string;
  clientLastName?: string;
  // Renamed from the old `clientLocation` (which confusingly carried the
  // CITY): the estimator's clientLocation state is now the Street Address,
  // so the contract props use unambiguous names. clientStreet → hdrAddress,
  // clientCity → hdrCity.
  clientStreet?: string;
  clientCity?: string;
  clientState?: string;
  clientZip?: string;
  clientPhone?: string;
  clientEmail?: string;
  demoType?: string | null;
  demoDescription?: string | null;
  addItemsDetailed?: any;
  /** App's LIVE settings (company name, logo, address, the signer's name and
   *  signature). Read on every render so the contract always prints what
   *  Settings holds right now — never a copy taken when the page opened. */
  userSettings?: any;
  /** Org admin (org_members.role === 'admin'): may edit the standard
   *  contract wording. Everyone else gets no editing affordance. */
  canEditWording?: boolean;
  /** Saves the company-wide standard contract wording (App routes it into
   *  org_settings.proposal_text.contract through the settings save). */
  onContractTextChange?: (next: ContractText) => void;
  contractTextSaveState?: "idle" | "saving" | "saved" | "error";
};

const DEFAULT_LEGAL =
  "All material is guaranteed to be specified. All work to be completed in a work-manlike manner according to standard practices.\n\nThe buyer is responsible for all permits and C.O.'s unless otherwise specified. Decks Unique Inc. is not responsible for weathering, shrinkage or growth on materials, or any underground utilities that may be damaged.\n\nAll agreements contingent upon strikes, accidents or delays beyond our control. There will be a labor charge for any warrantee claim.\n\nIn the event of any litigation to enforce the terms of this contract the unsuccessful party will reimburse the other party for all costs, including reasonable attorney fees.";

const DEFAULT_PAYMENT_TEXT = "$1,000 deposit with contract. Balance upon completion.";

export const defaultContract = (): ContractData => ({
  hdrClient: "",
  hdrAddress: "",
  hdrCity: "",
  hdrState: "",
  hdrZip: "",
  hdrPhone: "",
  hdrEmail: "",
  hdrDate: new Date().toLocaleDateString(),
  hdrDateTouched: false,
  hdrPageNum: "1",
  hdrPageOf: "1",
  hdrTouched: {},
  hdrApproxStart: "",
  hdrApproxEnd: "",
  hdrEssence: "not",
  specificationText: "",
  specificationTouched: false,
  contractSumWords: "",
  contractSumNumerals: "",
  paymentScheduleText: DEFAULT_PAYMENT_TEXT,
  paymentMode: "basic",
  paymentStages: [
    { id: "deposit", label: "Deposit", pct: 10 },
    { id: "dayOne", label: "Day one", pct: 30 },
    { id: "afterDecking", label: "After decking completed", pct: 30 },
    { id: "holdback", label: "Holdback after final inspection", pct: 0 },
  ],
  paymentPercents: { deposit: 10, dayOne: 30, afterDecking: 30, holdback: 0 },
  paymentLabels: {
    deposit: "Deposit",
    dayOne: "Day one",
    afterDecking: "After decking completed",
    holdback: "Holdback after final inspection",
    balance: "Balance upon completion",
  },
  legalDisclaimerText: DEFAULT_LEGAL,
  forcePageBreak: false,
  st124ProjectDescription: "",
  st124ProjectDescriptionTouched: false,
});

/** Resolve the staged payment schedule into a stage array. Uses the dynamic
 *  `paymentStages` when present; otherwise (old contracts saved before that
 *  field existed) derives it from the legacy fixed paymentPercents/
 *  paymentLabels with stable, deterministic ids so React keys don't churn. */
export const getStages = (c: ContractData): PaymentStage[] => {
  if (Array.isArray(c.paymentStages)) return c.paymentStages;
  return [
    { id: "deposit", label: c.paymentLabels.deposit, pct: c.paymentPercents.deposit },
    { id: "dayOne", label: c.paymentLabels.dayOne, pct: c.paymentPercents.dayOne },
    {
      id: "afterDecking",
      label: c.paymentLabels.afterDecking,
      pct: c.paymentPercents.afterDecking,
    },
    {
      id: "holdback",
      label: c.paymentLabels.holdback,
      pct: c.paymentPercents.holdback,
    },
  ];
};

/** Stable unique id for a newly-added stage. Used only in event handlers
 *  (never during render), so crypto.randomUUID is safe here. */
const newStageId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `stage-${Math.round(Math.random() * 1e9)}`;

/** Display a percentage rounded to the tenths place, with any trailing ".0"
 *  trimmed: 1.1001 → "1.1", 48.8999 → "48.9", 25 → "25". This is DISPLAY
 *  ONLY — the canonical pct (and the dollar amount derived from it) keep full
 *  precision, so rounding the shown % never drifts the customer's dollars. */
const fmtPct = (v: number): string => {
  const rounded = Math.round((Number(v) || 0) * 10) / 10;
  return String(rounded);
};

// Long date with a "___" placeholder when the source field is empty —
// used in the "Prepared for" paragraph so missing dates don't read as
// dropped sentence fragments.
// The contract date takes the year closest to today ("9/28" typed on Oct 1
// is this September); start/end dates take the next occurrence.
const longOrBlank = (raw: string, guess: YearGuess = "next"): string =>
  formatLongDate(raw, new Date(), guess) || "___";

// Format a 10-digit US phone number as "(XXX) XXX-XXXX" for display.
// Strips non-digit characters first so messy input ("631-455-7463",
// "(631)4557463") normalizes consistently. If the digit count isn't
// exactly 10, returns the raw input untouched so partial/extension/
// country-code numbers display as typed instead of being mangled.
const formatPhone = (raw: string): string => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

// Expand a 2-letter state code to its full name for the "Prepared for"
// paragraph. NY-only by design — Decks Unique only operates in NY, and
// other 2-letter codes are kept as-is to avoid silently mis-expanding.
const expandStateName = (raw: string): string => {
  const s = (raw || "").trim();
  if (!s) return "";
  return s.toUpperCase() === "NY" ? "New York" : s;
};

const numberToWords = (num: number): string => {
  if (!Number.isFinite(num) || num <= 0) return "";
  const belowTwenty = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight",
    "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
    "Sixteen", "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const thousandPowers = ["", "Thousand", "Million", "Billion"];

  const chunkToWords = (n: number) => {
    let out = "";
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    if (hundred) out += `${belowTwenty[hundred]} Hundred`;
    if (rest) {
      if (out) out += " ";
      if (rest < 20) out += belowTwenty[rest];
      else {
        const t = Math.floor(rest / 10);
        const u = rest % 10;
        out += tens[t];
        if (u) out += ` ${belowTwenty[u]}`;
      }
    }
    return out;
  };

  let n = Math.floor(num);
  let power = 0;
  const words: string[] = [];
  while (n > 0) {
    const chunk = n % 1000;
    if (chunk) {
      const chunkWords = chunkToWords(chunk);
      const label = thousandPowers[power];
      words.unshift(label ? `${chunkWords} ${label}` : chunkWords);
    }
    n = Math.floor(n / 1000);
    power += 1;
  }
  return words.join(" ").trim();
};

export default function ContractPage(props: Props) {
  // Fully controlled: derive contract from prop, fall back to defaults.
  const c: ContractData = useMemo(
    () => props.contract ?? defaultContract(),
    [props.contract]
  );

  // Multiple auto-fill effects (client name, city, email, spec, ST-124) all
  // fire on mount and each call update(). Without a ref, each effect's `update`
  // closure would spread the same stale `c` from render time — so the last
  // effect to run would overwrite all earlier patches (e.g. the ST-124 effect
  // would clobber the spec text). Sync the ref through every update so
  // patches merge against the latest in-flight value, not the render-time one.
  const cRef = useRef<ContractData>(c);
  cRef.current = c;

  const update = (patch: Partial<ContractData>) => {
    const next = { ...cRef.current, ...patch };
    cRef.current = next;
    props.onContractChange(next);
  };
  // Same, for the auto-fill effects below. Visiting the Contract tab is not
  // an edit: without this, opening the tab right after a save re-flagged the
  // estimate as unsaved.
  const autoUpdate = (patch: Partial<ContractData>) => {
    const next = { ...cRef.current, ...patch };
    cRef.current = next;
    props.onContractChange(next, { auto: true });
  };

  // ----- Staged payment schedule mutations. All write the dynamic
  // paymentStages array (resolved via getStages, so the first edit on an old
  // contract migrates it). pct is canonical; the $ column derives from it.
  const setStages = (next: PaymentStage[]) => update({ paymentStages: next });

  const patchStage = (id: string, patch: Partial<PaymentStage>) =>
    setStages(getStages(cRef.current).map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addStage = () =>
    setStages([...getStages(cRef.current), { id: newStageId(), label: "", pct: 0 }]);

  const removeStage = (id: string) =>
    setStages(getStages(cRef.current).filter((s) => s.id !== id));

  // Edit a stage's dollar amount → convert to the canonical pct. Rounded to
  // 4 decimals so the derived $ round-trips to the typed value. No-op when
  // there's no contract total to derive a percentage from.
  const setStageAmount = (id: string, rawAmt: string) => {
    const total = Number(cRef.current.contractSumNumerals.replace(/[^\d]/g, "")) || 0;
    if (total <= 0) return;
    const amt = Number(String(rawAmt).replace(/[^\d]/g, "")) || 0;
    const pct = Math.round((amt / total) * 100 * 1e4) / 1e4;
    patchStage(id, { pct });
  };

  const [mode, setMode] = useState<"edit" | "preview" | "st124">("edit");

  // Session-only undo stack for SOW line removals (× button + Backspace
  // on empty line). Each entry records the line's text and the index it
  // occupied at removal time so undo restores it to its original slot.
  const [specUndoStack, setSpecUndoStack] = useState<
    { idx: number; text: string }[]
  >([]);

  // ============================================================
  // Branding + signature — from App's LIVE settings
  // ============================================================
  // This used to read the localStorage cache ONCE, when the page opened, and
  // checked the legacy "userSettings" key FIRST. So a contract opened before
  // the settings fetch landed kept whatever the cache held (possibly no
  // signature) until you left the page, and a machine still carrying that
  // legacy key — which nothing writes any more — printed its stale values
  // forever. Either way the Authorized Signature line could print blank or
  // wrong with nothing on screen to say so. Live props, like Email reading the
  // live handler: whatever Settings holds now is what the contract prints.
  // The cache read is only the fallback for a caller that passes no settings.
  const liveUserSettings = props.userSettings;
  const cachedUserSettings = useMemo(() => {
    if (liveUserSettings) return null;
    const tryRead = (key: string) => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };
    return (
      tryRead("userSettings") ||
      tryRead("du_user_settings") ||
      tryRead("duUserSettings") ||
      null
    );
  }, [liveUserSettings]);
  const storedUserSettings = liveUserSettings || cachedUserSettings;

  const companyName = useMemo(
    () =>
      (
        storedUserSettings?.companyName ||
        ""
      ).trim() || "Decks Unique",
    [storedUserSettings]
  );

  const companyLogo = useMemo(
    () => String(storedUserSettings?.logoDataUrl || "").trim(),
    [storedUserSettings]
  );

  const companyTagline = useMemo(
    () =>
      String(
        storedUserSettings?.logoSlogan ||
          "Pride and Quality Make Decks Unique"
      ).trim(),
    [storedUserSettings]
  );

  // ============================================================
  // Auto-fill: client name + city from estimate
  // ============================================================
  // Contract client name is First + Last ("John Smith") — NOT Title + Last.
  // (The proposal greeting still uses Title + Last; that lives in
  // ProposalPage and is intentionally left unchanged.)
  const autoClientName = useMemo(() => {
    return [props.clientFirstName, props.clientLastName]
      .map((p) => (p || "").trim())
      .filter(Boolean)
      .join(" ");
  }, [props.clientFirstName, props.clientLastName]);

  useEffect(() => {
    if (autoClientName && !c.hdrTouched.client && autoClientName !== c.hdrClient) {
      autoUpdate({ hdrClient: autoClientName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoClientName, c.hdrTouched.client]);

  useEffect(() => {
    const street = (props.clientStreet || "").trim();
    if (street && !c.hdrTouched.address && street !== c.hdrAddress) {
      autoUpdate({ hdrAddress: street });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientStreet, c.hdrTouched.address]);

  useEffect(() => {
    const city = (props.clientCity || "").trim();
    if (city && !c.hdrTouched.city && city !== c.hdrCity) {
      autoUpdate({ hdrCity: city });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientCity, c.hdrTouched.city]);

  useEffect(() => {
    const st = formatStateCode(props.clientState);
    if (st && !c.hdrTouched.state && st !== c.hdrState) {
      autoUpdate({ hdrState: st });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientState, c.hdrTouched.state]);

  useEffect(() => {
    const zip = (props.clientZip || "").trim();
    if (zip && !c.hdrTouched.zip && zip !== c.hdrZip) {
      autoUpdate({ hdrZip: zip });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientZip, c.hdrTouched.zip]);

  useEffect(() => {
    const phone = (props.clientPhone || "").trim();
    if (phone && !c.hdrTouched.phone && phone !== c.hdrPhone) {
      autoUpdate({ hdrPhone: phone });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientPhone, c.hdrTouched.phone]);

  useEffect(() => {
    const e = (props.clientEmail || "").trim();
    if (e && !c.hdrTouched.email && e !== c.hdrEmail) {
      autoUpdate({ hdrEmail: e });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clientEmail, c.hdrTouched.email]);

  // ============================================================
  // Auto-generate scope of work from estimate selections
  // ============================================================
  const addOnLabels = useMemo(() => {
    const items = (props.addItemsDetailed || [])
      .map((row: any) => {
        const pickedDesc = row?.picked?.proposal_description || "";
        const picked = row?.picked?.name || row?.picked?.label || "";
        const customName = row?.customName || "";
        const customDesc = row?.customDescription || "";
        const qty = Number(row?.qty ?? row?.quantity ?? row?.lineQty ?? 0);
        const lineBase = Number(row?.lineBase || 0);
        const customPrice = Number(row?.customPrice || 0);

        const label = (pickedDesc || customDesc || customName || picked || "").toString().trim();
        if (!label) return "";
        if (qty <= 0 && lineBase <= 0 && customPrice <= 0) return "";

        // No "(x91)" quantity suffix — Jay 11/5: the contract reads as a
        // specification, and a bare count after a description means nothing
        // to the customer. Quantities stay on the proposal.
        return label;
      })
      .filter((item: string) => item.trim() !== "");
    return Array.from(new Set(items));
  }, [props.addItemsDetailed]);

  // The standard-wording line open for editing (admin pencil), by key.
  const [stdKey, setStdKey] = useState<ContractTextKey | null>(null);
  // Pencils and "Pricing Admin" tags are hidden until the admin turns on
  // "Edit standard wording" (Jay 11/5: keep the list clean day to day).
  const [wordingMode, setWordingMode] = useState(false);
  const showWording = !!props.canEditWording && wordingMode;
  // Company-wide standard wording (admin-edited; missing key = built-in).
  const contractText = useMemo(
    () => normalizeContractText(props.userSettings?.proposalText?.contract),
    [props.userSettings?.proposalText?.contract]
  );

  // `keys` maps each generated line's text to the standard-wording key it
  // came from, so the line list can put an admin pencil on exactly those
  // lines (Pricing Admin lines have no key).
  const autoSpec = useMemo(() => {
    const lines: string[] = [];
    const keys: Record<string, ContractTextKey> = {};
    const add = (s?: string | null) => {
      const t = (s || "").trim();
      if (t) lines.push(t);
    };
    const line = (key: ContractTextKey, vars = {}) => {
      const t = contractLine(contractText, key, vars).trim();
      if (t && !(t in keys)) keys[t] = key;
      add(t);
    };

    line("opening");

    const demoName = (props.demoType || "").trim();
    const demoBlurb = (props.demoDescription || "").trim();
    if (demoBlurb) add(`Demolition: ${demoBlurb}`);
    else if (demoName) line("demoFallback", { demo: demoName });

    const decking = (props.selectedDecking?.name || props.selectedDecking?.label || "").trim();
    const fastener = (props.selectedFastener?.name || props.selectedFastener?.label || "").trim();
    const railing = (props.selectedRailing?.name || props.selectedRailing?.label || "").trim();
    const skirting = (props.selectedSkirting?.name || props.selectedSkirting?.label || "").trim();
    const stairs = (props.selectedStairOption?.name || props.selectedStairOption?.label || "").trim();

    // Matched on words: Pricing Admin spells the types "Second_Story",
    // "Sleeper System", "Second  Story Sleeper", which the old exact-string
    // checks missed (those jobs got no Deck Structure line at all).
    const structureKey = structureKeyFor(props.constructionType || "");
    if (structureKey) line(structureKey);

    if (decking) line("decking", { decking });
    if (fastener) {
      const f = fastener.toLowerCase();
      line(
        f.includes("hidden") || f.includes("clip")
          ? "fastenerClips"
          : f.includes("nail")
            ? "fastenerNails"
            : f.includes("scrail")
              ? "fastenerScrails"
              : f.includes("screw")
                ? "fastenerScrews"
                : "fastenerOther",
        { fastener }
      );
    }
    if (railing) {
      const r = railing.toLowerCase();
      const isTrexSelectFlatTop =
        r.includes("trex") && r.includes("select") && r.includes("flat");
      // Some rails name their color in the product name, so appending
      // ", color to be determined" reads wrong (e.g. "Trex Select TRail
      // wht_sq, color to be determined" — white is already specified).
      //
      // Decide by tokens (split on non-alphanumerics so "wht_sq" → wht/sq,
      // "blk_rd" → blk/rd, "(white)" → white):
      //   - a definite color token (white/wht/black/blk) means the color is
      //     specified → the no-color-note line;
      //   - BUT an "or" token marks an open choice ("wht or brz") → keep the
      //     note. "Colorguard" tokenizes whole, not "color".
      const tokens = new Set(r.split(/[^a-z0-9]+/).filter(Boolean));
      const namesAColor =
        tokens.has("white") ||
        tokens.has("wht") ||
        tokens.has("black") ||
        tokens.has("blk");
      const isOpenColorChoice = tokens.has("or"); // e.g. "wht or brz"
      const colorInName = namesAColor && !isOpenColorChoice;
      line(
        isTrexSelectFlatTop
          ? "railingSelectFlatTop"
          : colorInName
            ? "railingColorInName"
            : "railing",
        { railing }
      );
    }
    if (stairs) {
      const stairBlurb = (props.selectedStairOption?.proposal_description || "").trim();
      add(stairBlurb ? `Stairs: ${stairBlurb}` : `Stairs: ${stairs}.`);
    }
    if (skirting) {
      // Lattice-ness lives in the item's CATEGORY ("Lattice"), not its name
      // (lattice items are named "Colored Vinyl PF", "Cedar", etc.). Within
      // lattice, a "PF" name suffix means picture-framed.
      const skirtCategory = (props.selectedSkirting?.category || "")
        .toLowerCase()
        .trim();
      if (skirtCategory === "lattice") {
        line(/\bpf\b/i.test(skirting) ? "latticePictureFrame" : "lattice");
      } else {
        // Horizontal skirting replaces vertical — never both. Horizontal can
        // arrive two ways: as THIS selected skirting (name carries the "horiz"
        // stem — note the DB's "Horiztonal" misspelling), or as a separate
        // +Add Item whose own proposal_description renders the horizontal line.
        // In either case we must NOT also emit the vertical line. (The
        // vertical skirting here is often the silent decking→skirting
        // prefill, which is why both lines showed up.)
        const mainIsHorizontal = skirting.toLowerCase().includes("horiz");
        const hasHorizontalAddItem = (props.addItemsDetailed || []).some(
          (row: any) => {
            const n = (row?.picked?.name || "").toLowerCase();
            const d = (row?.picked?.proposal_description || "").toLowerCase();
            return n.includes("horiz") || d.includes("horizontal");
          }
        );
        if (mainIsHorizontal) line("skirtingHorizontal");
        else if (!hasHorizontalAddItem) line("skirtingVertical");
        // else: horizontal came from an +Add Item — its own description
        // provides the single horizontal line.
      }
    }

    addOnLabels.forEach((item) => add(item as string));
    return { text: Array.from(new Set(lines)).join("\n"), keys };
  }, [
    props.demoType,
    props.demoDescription,
    props.selectedDecking,
    props.selectedFastener,
    props.selectedRailing,
    props.selectedStairOption,
    props.selectedSkirting,
    props.constructionType,
    props.addItemsDetailed,
    addOnLabels,
    contractText,
  ]);
  const autoSpecification = autoSpec.text;

  // Seed scope text from auto-generated version unless user has real edits.
  // We treat an empty specificationText as "not yet populated" even when the
  // touched flag is true — that flag gets set by any edit (including clearing
  // all lines), so without this an empty-but-touched contract would never
  // auto-fill on reopen.
  useEffect(() => {
    const hasUserContent =
      c.specificationTouched && (c.specificationText || "").trim().length > 0;
    if (!hasUserContent && autoSpecification !== c.specificationText) {
      autoUpdate({
        specificationText: autoSpecification,
        specificationTouched: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSpecification, c.specificationTouched]);

  // ============================================================
  // ST-124 project description (auto-derived from selections)
  // ============================================================
  const autoST124Description = useMemo(() => {
    const decking = (props.selectedDecking?.name || "").trim();
    const railing = (props.selectedRailing?.name || "").trim();
    const construction = (props.constructionType || "").trim().toLowerCase();

    const verb =
      construction.includes("resurface") || construction.includes("rebuild")
        ? "Rebuild of"
        : construction.includes("second")
          ? "Second-story addition of"
          : "New construction of";

    let surface = "wood deck";
    if (decking) {
      const lower = decking.toLowerCase();
      const brand = decking.split(/\s+/)[0];
      if (
        lower.includes("ipe") ||
        lower.includes("mahogany") ||
        lower.includes("hardwood")
      ) {
        surface = "hardwood deck";
      } else if (
        lower.includes("trex") ||
        lower.includes("timbertech") ||
        lower.includes("azek") ||
        lower.includes("fiberon") ||
        lower.includes("composite") ||
        lower.includes("pvc")
      ) {
        surface = `${brand} composite deck`;
      } else if (lower.includes("kdat") || lower.includes("pressure")) {
        surface = "pressure-treated wood deck";
      } else {
        surface = `${brand} deck`;
      }
    }

    let railingDesc = "";
    if (railing) {
      const brand = railing.split(/\s+/)[0];
      railingDesc = ` with ${brand} railing system`;
    }

    return `${verb} ${surface}${railingDesc}, permanently affixed to real property.`;
  }, [props.selectedDecking, props.selectedRailing, props.constructionType]);

  useEffect(() => {
    if (
      !c.st124ProjectDescriptionTouched &&
      autoST124Description !== c.st124ProjectDescription
    ) {
      autoUpdate({ st124ProjectDescription: autoST124Description });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoST124Description, c.st124ProjectDescriptionTouched]);

  // Signature dates are no longer separate fields: a new contract's hdrDate
  // defaults to today (defaultContract) and a revised Save advances an
  // untouched hdrDate to that day (App.withRefreshedContractDate). Both
  // Acceptance-block signature lines and the ST-124 dates read hdrDate.

  // ============================================================
  // Derived values
  // ============================================================
  const specLines = useMemo(
    () =>
      c.specificationText
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l !== ""),
    [c.specificationText]
  );

  const paymentMath = useMemo(() => {
    const total = Number(c.contractSumNumerals.replace(/[^\d]/g, "")) || 0;
    const r = (v: number) => Math.round(v);
    const fmt = (v: number) => `$${v.toLocaleString()}`;

    // Per-stage: pct is canonical, $ is derived (round(total*pct/100)).
    const stages = getStages(c).map((s) => {
      const pct = Math.max(0, Number(s.pct) || 0);
      return { id: s.id, label: s.label, pct, amt: r((total * pct) / 100) };
    });

    const sumPct = stages.reduce((acc, s) => acc + s.pct, 0);
    const sumAmt = stages.reduce((acc, s) => acc + s.amt, 0);

    // Balance is the remainder so the schedule always totals the contract.
    // Rounding error is absorbed here, so Σ stage amounts + balance === total.
    const balPct = Math.max(0, 100 - sumPct);
    const balanceAmt = Math.max(total - sumAmt, 0);

    // True when the defined stages exceed the contract (balance pinned to 0).
    const overage = sumAmt > total || sumPct > 100;

    return { total, r, fmt, stages, sumPct, sumAmt, balPct, balanceAmt, overage };
    // getStages(c) only reads the four payment fields listed below; depending
    // on whole `c` would recompute on every unrelated contract edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.contractSumNumerals, c.paymentStages, c.paymentPercents, c.paymentLabels]);

  // Flattened sentence version of the staged payment schedule — used for the
  // printed contract so it reads like the old text-style schedule rather than a table.
  const stagedSentence = useMemo(() => {
    const { fmt, stages, balanceAmt } = paymentMath;
    // Printed schedule shows ONLY stage name + dollar amount — no percentages.
    // Stages lead with the dollar ("$500 deposit"); the balance trails with its
    // amount in parens ("balance upon completion ($22,224)").
    const parts = stages
      // Skip blank/$0 rows (e.g. a freshly-added, not-yet-filled stage).
      .filter((s) => s.amt > 0 && s.label.trim())
      .map((s) => `${fmt(s.amt)} ${s.label.toLowerCase()}`);
    parts.push(`${c.paymentLabels.balance.toLowerCase()} (${fmt(balanceAmt)})`);
    return parts.join(", ");
  }, [paymentMath, c.paymentLabels.balance]);

  // ============================================================
  // Actions
  // ============================================================

  // Remove a single SOW line cleanly (no merge into the previous line)
  // and push it onto the undo stack so it can be restored later.
  const removeSpecLine = (idx: number) => {
    const linesNow = (c.specificationText ?? "").split("\n");
    if (idx < 0 || idx >= linesNow.length) return;
    const removed = linesNow[idx];
    const nextLines = linesNow.slice();
    nextLines.splice(idx, 1);
    if (nextLines.length === 0) nextLines.push("");
    setSpecUndoStack((stack) => [...stack, { idx, text: removed }]);
    update({
      specificationText: nextLines.join("\n"),
      specificationTouched: true,
    });
  };

  // Pop the most recent removal off the stack and re-insert that line
  // at its original index (clamped to current length so it stays valid
  // after intervening edits).
  const undoSpecRemove = () => {
    if (specUndoStack.length === 0) return;
    const last = specUndoStack[specUndoStack.length - 1];
    const linesNow = (c.specificationText ?? "").split("\n");
    const nextLines = linesNow.slice();
    const insertIdx = Math.min(Math.max(0, last.idx), nextLines.length);
    nextLines.splice(insertIdx, 0, last.text);
    setSpecUndoStack(specUndoStack.slice(0, -1));
    update({
      specificationText: nextLines.join("\n"),
      specificationTouched: true,
    });
  };

  const setHdrField = (field: keyof ContractData["hdrTouched"], value: string) => {
    const map: Record<string, keyof ContractData> = {
      client: "hdrClient",
      address: "hdrAddress",
      city: "hdrCity",
      state: "hdrState",
      zip: "hdrZip",
      phone: "hdrPhone",
      email: "hdrEmail",
    };
    update({
      hdrTouched: { ...c.hdrTouched, [field]: true },
      [map[field]]: value,
    } as Partial<ContractData>);
  };

  const handleReset = () => {
    if (
      !window.confirm(
        "Reset contract to defaults? Client info, scope edits, schedule, and payment settings will be cleared."
      )
    )
      return;
    props.onContractChange(defaultContract());
  };

  // Inject contract-only @page rules right before printing, then remove them on
  // afterprint. CSS @page rules can't be scoped to a component via selectors —
  // if we left them in the static stylesheet they'd apply to every print job in
  // the app (e.g. the Proposal page would get the contract's "Decks Unique Inc.
  // — Contract" running header).
  // Props fed to <ST124Form />. Cap Improv tab now prints the HTML form
  // directly — no pdf-lib generator. Shape matches ST124FormProps exactly
  // so we can spread it: <ST124Form {...st124Data} />.
  const st124Data = useMemo<ST124FormProps>(() => {
    // Always M/D/YYYY with the year, even when the contract date was typed
    // as just "10/1".
    const today =
      formatShortDate(c.hdrDate) || formatShortDate(new Date().toLocaleDateString("en-US"));
    return {
      contractor: {
        name: companyName || "Decks Unique Inc.",
        address: storedUserSettings?.companyAddress || "119 Commack Road",
        city: "Commack",
        state: "NY",
        zip: "11725",
      },
      customer: {
        name: c.hdrClient || "",
        address: c.hdrAddress || "",
        city: c.hdrCity || "",
        state: formatStateCode(c.hdrState) || "NY",
        zip: c.hdrZip || "",
      },
      // Once the user has touched the field, respect their value verbatim
      // (including an intentionally-cleared blank). Until then, show the
      // stored value or fall back to the live auto-generated description
      // (never a stale hardcoded literal).
      description: c.st124ProjectDescriptionTouched
        ? c.st124ProjectDescription
        : c.st124ProjectDescription || autoST124Description,
      project: {
        name: c.hdrClient ? `${c.hdrClient} Residence` : "",
        address: c.hdrAddress || "",
        city: c.hdrCity || "",
        state: formatStateCode(c.hdrState) || "NY",
        zip: c.hdrZip || "",
      },
      isOwner: true,
      contractIncludesItems: false,
      customerSignature: {
        title: "Homeowner",
        date: today,
      },
      contractorSignature: {
        imageUrl: storedUserSettings?.signatureUrl || undefined,
        title: "Contractor",
        date: today,
      },
    };
  }, [
    c.hdrClient,
    c.hdrAddress,
    c.hdrCity,
    c.hdrState,
    c.hdrZip,
    c.hdrDate,
    c.st124ProjectDescription,
    c.st124ProjectDescriptionTouched,
    autoST124Description,
    companyName,
    storedUserSettings?.companyAddress,
    storedUserSettings?.signatureUrl,
  ]);

  const printContract = () => {
    const STYLE_ID = "du-contract-print-style";

    // Remove any leftover style from a previous attempt
    document.getElementById(STYLE_ID)?.remove();

    // Cap Improv (ST-124) is a single-page tax document — skip the contract's
    // @page injection (running header + "N of N" page counter) and just print
    // the form. The ST124Form.css print block handles its own page sizing.
    if (mode === "st124") {
      window.print();
      return;
    }

    // After the st124 early-return above, mode is "edit" | "preview" — both
    // print the same contract document, so the running header is constant.
    const runningHeader = "Decks Unique Inc.  —  Contract";

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.setAttribute("media", "print");
    style.textContent = `
      /* Page 1 gets a tighter top margin (no running header to make room for);
         pages 2+ keep enough top room for the italic running header. */
      @page {
        size: letter;
        margin: 0.6in 0.6in 0.55in 0.6in;

        @top-left {
          content: "${runningHeader.replace(/—/g, "\\2014")}";
          font-family: "EB Garamond", Garamond, "Times New Roman", serif;
          font-size: 9pt;
          font-style: italic;
          color: #6b6f76;
          padding-bottom: 10pt;
          vertical-align: bottom;
        }

        @bottom-center {
          content: counter(page) " of " counter(pages);
          font-family: "EB Garamond", Garamond, "Times New Roman", serif;
          font-size: 9pt;
          font-style: italic;
          color: #6b6f76;
          padding-top: 8pt;
          vertical-align: top;
        }
      }

      @page :first {
        margin-top: 0.4in;
        @top-left { content: ""; }
      }
    `;
    document.head.appendChild(style);

    const cleanup = () => {
      document.getElementById(STYLE_ID)?.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);

    window.print();
  };

  // ST-124 description editor: the text the editor binds to (and the print
  // form renders). Mirrors st124Data.description so the on-screen editor and
  // the paper preview stay in lockstep.
  const st124DescValue = c.st124ProjectDescriptionTouched
    ? c.st124ProjectDescription
    : c.st124ProjectDescription || autoST124Description;
  // Warn (non-blocking) when the text would spill past the fixed 4-row block
  // on the printed form. wrapDescription still hard-truncates so the page
  // layout can never break — this just tells the user some text won't print.
  const st124DescOverflow =
    wrapDescriptionLines(st124DescValue).length > MAX_DESC_LINES;

  // ============================================================
  // Render
  // ============================================================
  return (
    <div
      className={`contract-page contract-mode-${mode}`}
      data-mode={mode}
      data-theme="light"
    >
      {/* ============================ Toolbar ============================ */}
      <div className="contract-toolbar no-print">
        <div className="contract-toolbar-left">
          <h1 className="contract-toolbar-title">Contract</h1>
          {c.hdrClient && (
            <span className="contract-toolbar-meta">· {c.hdrClient}</span>
          )}
        </div>

        <div className="contract-toolbar-center">
          <div className="contract-mode-switch" role="tablist" aria-label="View mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "edit"}
              className={`contract-mode-pill ${mode === "edit" ? "active" : ""}`}
              onClick={() => setMode("edit")}
            >
              Edit
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "preview"}
              className={`contract-mode-pill ${mode === "preview" ? "active" : ""}`}
              onClick={() => setMode("preview")}
            >
              Preview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "st124"}
              className={`contract-mode-pill ${mode === "st124" ? "active" : ""}`}
              onClick={() => setMode("st124")}
              title="NYS Form ST-124 Certificate of Capital Improvement"
            >
              Cap Improv
            </button>
          </div>
        </div>

        <div className="contract-toolbar-right">
          <button
            type="button"
            className="contract-btn contract-btn-primary"
            onClick={printContract}
          >
            Print
          </button>
          <button
            type="button"
            className="contract-btn contract-btn-ghost"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>

      {/* ============================ EDIT MODE ============================ */}
      {mode === "edit" && (
        <div className="contract-edit no-print">
          {/* Client */}
          <section className="contract-card">
            <header className="contract-card-header">
              <h2>Client & Job Site</h2>
              {autoClientName && !c.hdrTouched.client && (
                <span className="contract-pill contract-pill-auto">
                  Auto-filled from estimate
                </span>
              )}
            </header>

            <div className="contract-grid contract-grid-2">
              <FieldInput
                label="Client name"
                value={c.hdrClient}
                onChange={(v) => setHdrField("client", v)}
                placeholder="John &amp; Jane Smith"
              />
              <FieldInput
                label="Phone"
                value={c.hdrPhone}
                onChange={(v) => setHdrField("phone", v)}
                placeholder="(631) 555-1234"
              />
            </div>

            <div className="contract-grid contract-grid-2">
              <FieldInput
                label="Street address"
                value={c.hdrAddress}
                onChange={(v) => setHdrField("address", v)}
                placeholder="123 Main St"
              />
              <FieldInput
                label="Email"
                value={c.hdrEmail}
                onChange={(v) => setHdrField("email", v)}
                placeholder="smith@example.com"
              />
            </div>

            <div className="contract-grid contract-grid-3">
              <FieldInput
                label="City"
                value={c.hdrCity}
                onChange={(v) => setHdrField("city", v)}
                placeholder="Huntington"
              />
              <FieldInput
                label="State"
                value={c.hdrState}
                onChange={(v) => setHdrField("state", v)}
                placeholder="NY"
              />
              <FieldInput
                label="ZIP"
                value={c.hdrZip}
                onChange={(v) => setHdrField("zip", v)}
                placeholder="11743"
              />
            </div>

            {/* Single date drives everything: the prepared-for paragraph, both
                Acceptance-block signature lines, and the ST-124 dates. Editing
                it sets hdrDateTouched, which pins it against the save-time
                refresh-to-today. */}
            <FieldInput
              label="Contract date"
              value={c.hdrDate}
              onChange={(v) => update({ hdrDate: v, hdrDateTouched: true })}
            />
          </section>

          {/* Schedule */}
          <section className="contract-card">
            <header className="contract-card-header">
              <h2>Schedule</h2>
            </header>
            <div className="contract-grid contract-grid-2">
              <FieldInput
                label="Approximate start"
                value={c.hdrApproxStart}
                onChange={(v) => update({ hdrApproxStart: v })}
                placeholder="Jun 15, 2026"
              />
              <FieldInput
                label="Approximate end"
                value={c.hdrApproxEnd}
                onChange={(v) => update({ hdrApproxEnd: v })}
                placeholder="Jul 30, 2026"
              />
            </div>
            <div className="contract-radio-row">
              <label className="contract-radio">
                <input
                  type="radio"
                  name="essence"
                  checked={c.hdrEssence === "not"}
                  onChange={() => update({ hdrEssence: "not" })}
                />
                <span>
                  Completion date <strong>is NOT</strong> of the essence
                </span>
              </label>
              <label className="contract-radio">
                <input
                  type="radio"
                  name="essence"
                  checked={c.hdrEssence === "yes"}
                  onChange={() => update({ hdrEssence: "yes" })}
                />
                <span>
                  Completion date <strong>IS</strong> of the essence
                </span>
              </label>
            </div>
          </section>

          {/* Scope */}
          <section className="contract-card">
            <header className="contract-card-header">
              <h2>We hereby submit specification for:</h2>
              <div className="contract-card-header-right">
                {props.canEditWording ? (
                  <button
                    type="button"
                    className={`contract-wording-toggle${wordingMode ? " is-on" : ""}`}
                    aria-pressed={wordingMode}
                    onClick={() => {
                      setWordingMode((v) => !v);
                      setStdKey(null);
                    }}
                    title="Show a pencil on each standard sentence — changes apply to all future contracts"
                  >
                    {wordingMode ? "✓ Done editing wording" : "✎ Edit standard wording"}
                  </button>
                ) : null}
                {specUndoStack.length > 0 && (
                  <button
                    type="button"
                    className="contract-link-btn"
                    onClick={undoSpecRemove}
                    title={`Undo last removal (${specUndoStack.length} available)`}
                    aria-label="Undo last line removal"
                  >
                    ↶ Undo
                  </button>
                )}
                {!c.specificationTouched ? (
                  <span className="contract-pill contract-pill-auto">
                    Auto-generated
                  </span>
                ) : (
                  <button
                    type="button"
                    className="contract-link-btn"
                    onClick={() => {
                      setSpecUndoStack([]);
                      update({
                        specificationText: autoSpecification,
                        specificationTouched: false,
                      });
                    }}
                  >
                    ↻ Reset to auto
                  </button>
                )}
              </div>
            </header>
            <SpecLineEditor
              value={c.specificationText}
              // Admin only: pencil on lines that come from standard wording;
              // a "Pricing Admin" tag on lines that come from item
              // descriptions. Everyone else sees the plain list.
              standardKeyFor={
                showWording ? (l: string) => autoSpec.keys[l.trim()] : undefined
              }
              isPricingAdminLine={
                showWording
                  ? (l: string) => {
                      const t = l.trim();
                      return (
                        !!t &&
                        !(t in autoSpec.keys) &&
                        autoSpecification.split("\n").includes(t)
                      );
                    }
                  : undefined
              }
              openStandardKey={stdKey}
              onOpenStandard={setStdKey}
              renderStandardEditor={(key) => (
                <StandardLineEditor
                  textKey={key}
                  value={contractText}
                  onChange={(next) => props.onContractTextChange?.(next)}
                  saveState={props.contractTextSaveState}
                  contractHandEdited={!!c.specificationTouched}
                  onDone={() => setStdKey(null)}
                />
              )}
              onChange={(v) =>
                update({ specificationText: v, specificationTouched: true })
              }
              onRemoveLine={removeSpecLine}
            />
            <div className="contract-meta-row">
              <span className="contract-meta-text">
                {specLines.length} line{specLines.length === 1 ? "" : "s"}
              </span>
              <label className="contract-check">
                <input
                  type="checkbox"
                  checked={c.forcePageBreak}
                  onChange={(e) => update({ forcePageBreak: e.target.checked })}
                />
                <span>Force payment terms onto page 2 when printing</span>
              </label>
            </div>
          </section>

          {/* Sum & Payment */}
          <section className="contract-card">
            <header className="contract-card-header">
              <h2>Contract Sum &amp; Payment</h2>
            </header>

            <div className="contract-grid contract-grid-2">
              <div className="contract-field">
                <label className="contract-field-label">Total amount</label>
                <div className="contract-money">
                  <span className="contract-money-prefix">$</span>
                  <input
                    className="contract-input contract-input-money"
                    value={c.contractSumNumerals}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^\d]/g, "");
                      const num = raw ? Number(raw) : 0;
                      update({
                        contractSumNumerals: raw ? num.toLocaleString("en-US") : "",
                        contractSumWords: numberToWords(num),
                      });
                    }}
                    placeholder="25,500"
                    inputMode="decimal"
                  />
                </div>
                {c.contractSumWords && (
                  <div className="contract-field-helper">
                    {c.contractSumWords} USD 00/100
                  </div>
                )}
              </div>

              <div className="contract-field">
                <label className="contract-field-label">Payment schedule</label>
                <div className="contract-mode-switch contract-payment-switch">
                  <button
                    type="button"
                    className={`contract-mode-pill ${c.paymentMode === "basic" ? "active" : ""}`}
                    onClick={() => update({ paymentMode: "basic" })}
                  >
                    Basic
                  </button>
                  <button
                    type="button"
                    className={`contract-mode-pill ${c.paymentMode === "staged" ? "active" : ""}`}
                    onClick={() => update({ paymentMode: "staged" })}
                  >
                    Staged
                  </button>
                </div>
              </div>
            </div>

            {c.paymentMode === "basic" ? (
              <div className="contract-field">
                <label className="contract-field-label">Schedule description</label>
                <textarea
                  className="contract-textarea contract-textarea-sm"
                  value={c.paymentScheduleText}
                  onChange={(e) => update({ paymentScheduleText: e.target.value })}
                  rows={2}
                  placeholder={DEFAULT_PAYMENT_TEXT}
                />
              </div>
            ) : (
              <div className="contract-paytable">
                <div className="contract-paytable-head">
                  <div>Stage</div>
                  <div>%</div>
                  <div>Amount</div>
                  <div className="contract-paytable-rm-head" aria-hidden="true"></div>
                </div>
                {paymentMath.stages.map((s) => (
                  <div className="contract-paytable-row" key={s.id}>
                    <input
                      className="contract-input"
                      value={s.label}
                      placeholder="Stage name"
                      onChange={(e) => patchStage(s.id, { label: e.target.value })}
                    />
                    <StagedPercentInput
                      pct={s.pct}
                      onCommit={(v) => patchStage(s.id, { pct: v })}
                    />
                    <div className="contract-pct contract-amt-input">
                      <span>$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="contract-input"
                        value={s.amt || ""}
                        placeholder="0"
                        disabled={paymentMath.total <= 0}
                        title={
                          paymentMath.total <= 0
                            ? "Enter the Contract Sum amount first to edit dollar values"
                            : undefined
                        }
                        onChange={(e) => setStageAmount(s.id, e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="contract-paytable-remove"
                      title="Remove stage"
                      aria-label="Remove stage"
                      onClick={() => removeStage(s.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="contract-paytable-add"
                  onClick={addStage}
                >
                  + Add stage
                </button>
                <div className="contract-paytable-row contract-paytable-balance">
                  <input
                    className="contract-input"
                    value={c.paymentLabels.balance}
                    onChange={(e) =>
                      update({
                        paymentLabels: { ...c.paymentLabels, balance: e.target.value },
                      })
                    }
                  />
                  <div className="contract-pct contract-pct-readonly">
                    {fmtPct(paymentMath.balPct)}%
                  </div>
                  <div className="contract-paytable-amt">
                    {paymentMath.fmt(paymentMath.balanceAmt)}
                  </div>
                  <div className="contract-paytable-rm-head" aria-hidden="true"></div>
                </div>
                {paymentMath.overage && (
                  <div className="contract-paytable-warn">
                    ⚠ Stages exceed the contract total — Balance is $0. Reduce a stage so
                    the schedule totals {paymentMath.fmt(paymentMath.total)}.
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Legal */}
          <section className="contract-card">
            <header className="contract-card-header">
              <h2>Terms &amp; Conditions</h2>
              <button
                type="button"
                className="contract-link-btn"
                onClick={() => update({ legalDisclaimerText: DEFAULT_LEGAL })}
              >
                ↻ Reset to default
              </button>
            </header>
            <textarea
              className="contract-textarea"
              value={c.legalDisclaimerText}
              onChange={(e) =>
                update({
                  legalDisclaimerText: e.target.value.replace(/\n\s*\n/g, "\n"),
                })
              }
              rows={8}
            />
          </section>
        </div>
      )}

      {/* ============================ PREVIEW / PRINT ============================ */}
      <div
        className={`contract-preview-wrap ${mode === "preview" ? "" : "contract-preview-hidden"}`}
      >
        <article className="contract-doc">
          {/* Doc header — 3-column letterhead:
              LEFT  : logo only
              CENTER: CONTRACT title
              RIGHT : company address + phone */}
          <header className="contract-doc-header">
            <div className="contract-doc-header-row">
              <div className="contract-doc-header-left">
                <img
                  className="contract-doc-logo"
                  src={companyLogo || "/DU-log.png"}
                  alt={`${companyName} logo`}
                />
              </div>
              <div className="contract-doc-header-center">
                <div className="contract-doc-title">Contract</div>
              </div>
              <div className="contract-doc-header-right">
                <div className="contract-doc-header-companyline contract-doc-header-companyline--name">
                  Decks Unique Inc.
                </div>
                <div className="contract-doc-header-companyline">
                  119 Commack Road
                </div>
                <div className="contract-doc-header-companyline">
                  Commack, NY 11725
                </div>
                <div className="contract-doc-header-companyline">
                  631.266.3004
                </div>
              </div>
            </div>
            <div className="contract-doc-divider" aria-hidden="true" />
          </header>

          {/* Prepared-for paragraph — single flowing sentence with all auto-
              populated values in <strong>. Replaces the old label/value list.
              Missing required fields render as "___" so the paragraph still
              reads naturally and doesn't silently drop content. */}
          <section className="contract-doc-summary contract-doc-summary--card">
            <p className="contract-doc-body contract-doc-prepared">
              Prepared for <strong>{c.hdrClient || "___"}</strong> on{" "}
              <strong>{longOrBlank(c.hdrDate, "nearest")}</strong>, for the property
              located at <strong>{c.hdrAddress || "___"}</strong>,{" "}
              <strong>{c.hdrCity || "___"}</strong>,{" "}
              <strong>{expandStateName(formatStateCode(c.hdrState)) || "___"}</strong>{" "}
              <strong>{c.hdrZip || "___"}</strong>
              {(() => {
                const p = formatPhone((c.hdrPhone || "").trim());
                const e = (c.hdrEmail || "").trim();
                if (p && e)
                  return (
                    <>
                      {" "}
                      (phone: <strong>{p}</strong>, email:{" "}
                      <strong>{e}</strong>)
                    </>
                  );
                if (p)
                  return (
                    <>
                      {" "}
                      (phone: <strong>{p}</strong>)
                    </>
                  );
                if (e)
                  return (
                    <>
                      {" "}
                      (email: <strong>{e}</strong>)
                    </>
                  );
                return null;
              })()}
              . The approximate start date will be{" "}
              <strong>{longOrBlank(c.hdrApproxStart)}</strong> and the project
              is expected to be complete by{" "}
              <strong>{longOrBlank(c.hdrApproxEnd)}</strong>.{" "}
              {c.hdrEssence === "yes"
                ? "Time is of the essence for completion of this contract."
                : "The Contractor and the owner have determined that a definite completion date is NOT of the essence."}
            </p>
          </section>

          {/* Scope */}
          <section className="contract-doc-section contract-doc-section--scope">
            <h2 className="contract-doc-h2">We hereby submit specification for:</h2>
            {specLines.length === 0 ? (
              <p className="contract-doc-body contract-doc-empty">
                Add specifications in Edit mode.
              </p>
            ) : (
              <ul className="contract-doc-scope">
                {specLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </section>

          {(c.forcePageBreak || specLines.length > 15) && (
            <div className="contract-doc-pagebreak" />
          )}

          {/* Contract Sum — body sentence + words on the left,
              dollar amount right-aligned on the same row */}
          <section className="contract-doc-section">
            <h2 className="contract-doc-h2">Contract Sum</h2>
            <div className="contract-doc-sumcombined">
              <p className="contract-doc-sumcombined-text">
                We propose to furnish material and labor — complete in accordance
                with the above specifications — for the sum of:{" "}
                <em className="contract-doc-sumcombined-words">
                  {c.contractSumWords
                    ? `${c.contractSumWords} USD 00/100`
                    : "—"}
                </em>
              </p>
              <div className="contract-doc-sumcombined-amount">
                ${c.contractSumNumerals || "—"}
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="contract-doc-section">
            <h2 className="contract-doc-h2">Payment Schedule</h2>
            {/* Screen-only: keep the editable-table preview if staged */}
            {c.paymentMode === "staged" ? (
              <table className="contract-doc-paytable screen-only">
                <tbody>
                  {paymentMath.stages
                    .filter((s) => s.amt > 0 && s.label.trim())
                    .map((s) => (
                      <tr key={s.id}>
                        <td>{s.label}</td>
                        <td>{paymentMath.fmt(s.amt)}</td>
                      </tr>
                    ))}
                  <tr className="contract-doc-paytable-balance">
                    <td>{c.paymentLabels.balance}</td>
                    <td>{paymentMath.fmt(paymentMath.balanceAmt)}</td>
                  </tr>
                </tbody>
              </table>
            ) : null}

            {/* Plain text line — always shown in print, also shown on screen for basic mode */}
            <p
              className={`contract-doc-body contract-doc-payline ${c.paymentMode === "staged" ? "print-only" : ""}`}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {c.paymentMode === "basic" ? c.paymentScheduleText : stagedSentence}
            </p>
          </section>

          {/* Terms */}
          <section className="contract-doc-section">
            <h2 className="contract-doc-h2">Terms &amp; Conditions</h2>
            <p className="contract-doc-legal">
              {c.legalDisclaimerText.replace(/\n+/g, " ").trim()}
            </p>
          </section>

          {/* Acceptance */}
          <section className="contract-doc-section contract-doc-acceptance">
            <h2 className="contract-doc-h2">Acceptance of Proposal</h2>
            <p className="contract-doc-body">
              I have read this document and accept the prices, specifications, and
              conditions stated. I understand that upon signing this becomes a binding
              contract. You are authorized to do the work as specified. Payment will be
              made as outlined above.
            </p>
            <div className="contract-doc-sig-row">
              <div className="contract-doc-sig-unit">
                <div className="contract-doc-sig-lines">
                  <div className="contract-doc-sig-line contract-doc-sig-line--main" />
                  <div className="contract-doc-sig-line contract-doc-sig-line--date">
                    <span className="contract-doc-sig-datetext">
                      {formatShortDate(c.hdrDate)}
                    </span>
                  </div>
                </div>
                <div className="contract-doc-sig-labels">
                  <span>Client Signature</span>
                  <span>Date</span>
                </div>
                <div className="contract-doc-sig-printed">
                  <span className="contract-signature-printed-name">
                    {c.hdrClient}
                  </span>
                  <span aria-hidden="true" />
                </div>
              </div>
              <div className="contract-doc-sig-unit">
                <div className="contract-doc-sig-lines">
                  <div className="contract-doc-sig-line contract-doc-sig-line--main">
                    <SignatureImage
                      url={storedUserSettings?.signatureUrl}
                      className="contract-authorized-signature"
                    />
                  </div>
                  <div className="contract-doc-sig-line contract-doc-sig-line--date">
                    <span className="contract-doc-sig-datetext">
                      {formatShortDate(c.hdrDate)}
                    </span>
                  </div>
                </div>
                <div className="contract-doc-sig-labels">
                  <span>Authorized Signature</span>
                  <span>Date</span>
                </div>
                <div className="contract-doc-sig-printed">
                  <span className="contract-signature-printed-name">
                    {(storedUserSettings?.userName || "").trim() ||
                      "Jay Colapinto"}
                  </span>
                  <span aria-hidden="true" />
                </div>
              </div>
            </div>
          </section>

          {/* Cancellation */}
          <section className="contract-doc-cancel">
            <p>
              <strong>Notice of Cancellation.</strong> You, the buyer, may cancel
              this transaction at any time prior to midnight of the third (3rd)
              business day following the date of this agreement.
            </p>
          </section>

          {/* Footer */}
          <footer className="contract-doc-footer">
            <span>Nassau H18607600</span>
            <span>Suffolk 1614-H</span>
          </footer>
        </article>
      </div>

      {/* ============================ Cap Improv (ST-124) ============================
          Pure HTML/CSS recreation of the NY State form. Print is triggered
          by the contract page's main toolbar Print button — see
          printContract(), which short-circuits to window.print() in
          st124 mode. */}
      <div
        className={`contract-st124-wrap ${mode === "st124" ? "" : "contract-st124-hidden"}`}
      >
        {/* Screen-only bar — never prints (.no-print). The description is
            edited where it sits on the form below (click to edit, like the
            proposal page). Touching it stops the auto-sync effect from
            clobbering the edit when decking/railing/construction change. */}
        <div className="st124-editor no-print">
          <div className="st124-editor-head">
            <div
              className={`st124-editor-help ${
                st124DescOverflow ? "st124-editor-help-warn" : ""
              }`}
            >
              {st124DescOverflow
                ? `⚠ Too long for the form — only the first ${MAX_DESC_LINES} lines (≈${
                    MAX_DESC_LINE * MAX_DESC_LINES
                  } characters) will print.`
                : "Click the description on the form to edit it."}
            </div>
            {c.st124ProjectDescriptionTouched && (
              <button
                type="button"
                className="st124-editor-reset"
                onClick={() =>
                  update({
                    st124ProjectDescription: autoST124Description,
                    st124ProjectDescriptionTouched: false,
                  })
                }
              >
                Reset to auto
              </button>
            )}
          </div>
        </div>
        <div className="st124-screen">
          <ST124Form
            {...st124Data}
            onDescriptionChange={(next) =>
              update({
                st124ProjectDescription: next,
                st124ProjectDescriptionTouched: true,
              })
            }
            descriptionPlaceholder={autoST124Description}
          />
        </div>
      </div>
    </div>
  );
}

/** Percentage input for a staged payment row. Shows the value rounded to the
 *  tenths place when idle, but shows exactly what the user types while focused
 *  (no mid-keystroke rounding). Commits the typed value to the parent on each
 *  keystroke so the dollar column updates live. Crucially, if the user focuses
 *  and leaves WITHOUT typing, nothing is committed — so a $-derived
 *  full-precision pct (e.g. 1.1001%, shown as "1.1") is preserved and the
 *  customer's exact dollar amount never drifts. */
function StagedPercentInput({
  pct,
  onCommit,
}: {
  pct: number;
  onCommit: (next: number) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  return (
    <div className="contract-pct">
      <input
        type="number"
        className="contract-input"
        min={0}
        value={focused ? draft : fmtPct(pct)}
        onFocus={() => {
          setDraft(pct ? fmtPct(pct) : "");
          setFocused(true);
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          onCommit(Math.max(0, Number(e.target.value || 0)));
        }}
        onBlur={() => setFocused(false)}
      />
      <span>%</span>
    </div>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  placeholder,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="contract-field">
      <label className="contract-field-label">{label}</label>
      <input
        className="contract-input"
        type={type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

// ============================================================
// SpecLineEditor — line-by-line bullet editor.
// Splits the newline-joined value into rows, each its own input with a
// visible bullet. Enter on a row inserts a new empty row below (and
// focuses it). Backspace on an empty row removes it (and focuses the
// previous row at the end of its text). Multi-line paste is split into
// rows. Underlying data model is unchanged: a single string with \n
// separators stored in c.specificationText.
// ============================================================
function SpecLineEditor({
  value,
  onChange,
  onRemoveLine,
  standardKeyFor,
  isPricingAdminLine,
  openStandardKey,
  onOpenStandard,
  renderStandardEditor,
}: {
  value: string;
  onChange: (next: string) => void;
  onRemoveLine: (idx: number) => void;
  /** Admin only: the standard-wording key behind a line, if any. */
  standardKeyFor?: (line: string) => ContractTextKey | undefined;
  /** Admin only: the line comes from a Pricing Admin description. */
  isPricingAdminLine?: (line: string) => boolean;
  openStandardKey?: ContractTextKey | null;
  onOpenStandard?: (key: ContractTextKey) => void;
  renderStandardEditor?: (key: ContractTextKey) => React.ReactNode;
}) {
  const lines = useMemo(() => {
    const parts = (value ?? "").split("\n");
    return parts.length === 0 ? [""] : parts;
  }, [value]);

  const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const pendingFocus = useRef<{ idx: number; toEnd?: boolean } | null>(null);
  // Row the standard-wording editor opens under. Kept by position, not by
  // text: the line's text changes as the standard sentence is edited.
  const [stdIdx, setStdIdx] = useState<number | null>(null);

  // Auto-size every textarea to fit its content. Runs after each render
  // so newly-typed, pasted, removed, or restored lines all settle to the
  // right height with no scrollbars. useLayoutEffect avoids a visible
  // jump between the default 1-row height and the fitted height.
  useLayoutEffect(() => {
    inputRefs.current.forEach((el) => {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    });
  });

  useEffect(() => {
    if (!pendingFocus.current) return;
    const { idx, toEnd } = pendingFocus.current;
    pendingFocus.current = null;
    const el = inputRefs.current[idx];
    if (!el) return;
    el.focus();
    if (toEnd) {
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  });

  const commit = (next: string[]) => {
    onChange(next.join("\n"));
  };

  const setLine = (idx: number, val: string) => {
    const next = lines.slice();
    next[idx] = val;
    commit(next);
  };

  const insertAfter = (idx: number, initial = "") => {
    const next = lines.slice();
    next.splice(idx + 1, 0, initial);
    pendingFocus.current = { idx: idx + 1 };
    commit(next);
  };

  const onKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    // Enter splits the line at the caret into two rows (Shift+Enter is
    // reserved for an in-line soft break should anyone need one, though
    // the textarea will then wrap visually as a single bullet).
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const el = e.currentTarget;
      const caret = el.selectionStart ?? el.value.length;
      const before = el.value.slice(0, caret);
      const after = el.value.slice(caret);
      const next = lines.slice();
      next[idx] = before;
      next.splice(idx + 1, 0, after);
      pendingFocus.current = { idx: idx + 1 };
      commit(next);
      return;
    }
    // Backspace at the very start of an EMPTY line removes that line
    // cleanly (no merging text into the previous line) and routes the
    // removal through onRemoveLine so it lands on the undo stack.
    if (e.key === "Backspace") {
      const el = e.currentTarget;
      const caret = el.selectionStart ?? 0;
      const selEnd = el.selectionEnd ?? caret;
      if (caret === 0 && selEnd === 0 && el.value === "" && idx > 0) {
        e.preventDefault();
        pendingFocus.current = { idx: idx - 1, toEnd: true };
        onRemoveLine(idx);
      }
    }
  };

  const onPaste = (
    idx: number,
    e: React.ClipboardEvent<HTMLTextAreaElement>
  ) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text || !text.includes("\n")) return;
    e.preventDefault();
    const el = e.currentTarget;
    const caret = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, caret);
    const after = el.value.slice(caret);
    const pasteLines = text.split("\n");
    const next = lines.slice();
    if (pasteLines.length === 1) {
      next[idx] = before + pasteLines[0] + after;
      commit(next);
      return;
    }
    next.splice(
      idx,
      1,
      before + pasteLines[0],
      ...pasteLines.slice(1, -1),
      pasteLines[pasteLines.length - 1] + after
    );
    pendingFocus.current = { idx: idx + pasteLines.length - 1, toEnd: true };
    commit(next);
  };

  return (
    <div className={`contract-spec-editor${standardKeyFor ? " has-std" : ""}`}>
      {lines.map((line, idx) => (
        <div className="contract-spec-line" key={idx}>
          <span className="contract-spec-bullet" aria-hidden="true">
            •
          </span>
          <textarea
            ref={(el) => {
              inputRefs.current[idx] = el;
            }}
            className="contract-spec-input"
            rows={1}
            value={line}
            onChange={(e) => setLine(idx, e.target.value)}
            onKeyDown={(e) => onKeyDown(idx, e)}
            onPaste={(e) => onPaste(idx, e)}
            placeholder={idx === 0 ? "Add a specification line…" : ""}
            spellCheck
            autoCorrect="on"
            autoCapitalize="sentences"
            lang="en-US"
          />
          {(() => {
            const k = standardKeyFor?.(line);
            if (k && onOpenStandard) {
              const open = openStandardKey === k && stdIdx === idx;
              return (
                <button
                  type="button"
                  className={`contract-spec-std${open ? " is-open" : ""}`}
                  onClick={() => {
                    setStdIdx(idx);
                    onOpenStandard(k);
                  }}
                  aria-label="Edit the standard wording for this line (all future contracts)"
                  title="Edit standard wording — all future contracts"
                >
                  ✎
                </button>
              );
            }
            if (isPricingAdminLine?.(line)) {
              return (
                <span
                  className="contract-spec-src"
                  title="This line is the item's description in Pricing Admin — edit it there"
                >
                  Pricing Admin
                </span>
              );
            }
            return standardKeyFor ? <span className="contract-spec-std-gap" /> : null;
          })()}
          <button
            type="button"
            className="contract-spec-remove"
            onClick={() => {
              pendingFocus.current = {
                idx: Math.max(0, idx - 1),
                toEnd: true,
              };
              onRemoveLine(idx);
            }}
            aria-label="Remove this line"
            title="Remove line"
          >
            ×
          </button>
          {openStandardKey && stdIdx === idx && renderStandardEditor ? (
            <div className="contract-spec-std-editor">
              {renderStandardEditor(openStandardKey)}
            </div>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        className="contract-spec-add"
        onClick={() => insertAfter(lines.length - 1)}
      >
        + Add line
      </button>
    </div>
  );
}
